import { supabase } from '@/integrations/supabase/client'
import type { Session } from '@supabase/supabase-js'
import { useRoadStore } from '@/lib/roads/store'
import type { RouteTrims } from '@/lib/roads/trim'

export interface Profile {
  id: string
  username: string | null
  email: string | null
  created_at: string
}

export interface SavedRouteRow {
  id: string
  user_id: string
  road_ids: string[]
  saved_at: string
  /** Per-user display name — "[route UUID]-[user UUID]" row in route_names. */
  name: string | null
  /** Shared, account-independent totals keyed by the road sequence. */
  stats_miles: number | null
  stats_seconds: number | null
  stats_computed_at: string | null
  sort_index: number
  /** Saved per-road trims keyed by road id — null when the route is untrimmed. */
  trims: RouteTrims | null
}

/** Key for shared route_stats rows: the road sequence joined with ">". */
export function routeStatsKey(roadIds: string[]): string {
  return roadIds.join('>')
}

const STATS_MAX_AGE_MS = 30 * 60 * 1000

export function routeStatsFresh(row: Pick<SavedRouteRow, 'stats_computed_at'>): boolean {
  if (!row.stats_computed_at) return false
  return Date.now() - new Date(row.stats_computed_at).getTime() < STATS_MAX_AGE_MS
}

async function fetchRouteStats(keys: string[]): Promise<Map<string, { miles: number; seconds: number | null; computed_at: string }>> {
  const map = new Map<
    string,
    { miles: number; seconds: number | null; computed_at: string }
  >()
  if (keys.length === 0) return map
  const { data } = await supabase
    .from('route_stats')
    .select('route_key, miles, seconds, computed_at')
    .in('route_key', keys)
  for (const row of data ?? []) {
    map.set(row.route_key, {
      miles: row.miles,
      seconds: row.seconds,
      computed_at: row.computed_at,
    })
  }
  return map
}

/**
 * Persist freshly-computed shared totals for a road sequence. Best-effort:
 * if the caller's account doesn't have this exact route saved (or the write
 * fails) the stats simply stay unshared — nothing breaks.
 */
export async function saveRouteStats(
  roadIds: string[],
  miles: number,
  seconds: number | null,
): Promise<void> {
  try {
    await supabase.from('route_stats').upsert(
      {
        route_key: routeStatsKey(roadIds),
        miles,
        seconds,
        computed_at: new Date().toISOString(),
      },
      { onConflict: 'route_key' },
    )
  } catch {
    /* best-effort */
  }
}

// ── Helpers ──────────────────────────────────────────────────────────────────

export function isValidEmail(email: string): boolean {
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email.trim())
}

export function passwordProblem(pw: string): string | null {
  if (pw.length < 8) return 'Password must be at least 8 characters.'
  if (!/[A-Za-z]/.test(pw)) return 'Password must include at least one letter.'
  if (!/\d/.test(pw)) return 'Password must include at least one number.'
  return null
}

// True when the app was opened via a remote login / password-recovery email
// link (Supabase appends type=recovery to the URL). Captured once at module
// load, before Supabase strips the fragment from the URL.
export const recoveryArrival =
  typeof window !== 'undefined' &&
  /type=recovery/.test(`${window.location.hash}${window.location.search}`)

function friendlyAuthError(message: string): string {
  const m = message.toLowerCase()
  if (m.includes('user already registered') || m.includes('already been registered'))
    return 'An account with this email already exists.'
  if (m.includes('password') && m.includes('login'))
    return 'Invalid email/username or password.'
  if (m.includes('invalid login credentials')) return 'Invalid email/username or password.'
  if (m.includes('email not confirmed'))
    return 'Please confirm your email before signing in — or use "Enter backup code" below.'
  if (m.includes('rate limit')) return 'Too many attempts — please wait a moment and try again.'
  if (m.includes('user not found')) return 'No account found with that email address.'
  if (m.includes('token has expired') || m.includes('otp expired'))
    return 'That code has expired — request a new one.'
  if (m.includes('invalid') && m.includes('token'))
    return 'That code isn’t valid — check it and try again.'
  return message
}

// ── Session state ────────────────────────────────────────────────────────────

const listeners = new Set<() => void>()
let state: {
  session: Session | null
  profile: Profile | null
  /** true once the first profile fetch for the current session has settled */
  profileLoaded: boolean
  isModerator: boolean
  ready: boolean
} = {
  session: null,
  profile: null,
  profileLoaded: false,
  isModerator: false,
  ready: false,
}

function setState(patch: Partial<typeof state>) {
  state = { ...state, ...patch }
  listeners.forEach((l) => l())
}

function getProfile(session: Session | null): Promise<Profile | null> {
  if (!session) return Promise.resolve(null)
  return supabase
    .from('profiles')
    .select('id, username, email, created_at')
    .eq('id', session.user.id)
    .maybeSingle()
    .then(({ data }) => data as Profile | null)
}

async function refreshProfile() {
  const session = state.session
  const profile = await getProfile(session)
  // Moderators can force-reset individual roads from the road panel.
  let isModerator = false
  if (session) {
    const { data: roles } = await supabase
      .from('user_roles')
      .select('role')
      .eq('user_id', session.user.id)
    isModerator = (roles ?? []).some(
      (r: { role: string }) => r.role === 'moderator' || r.role === 'admin',
    )
  }
  setState({ profile, isModerator, profileLoaded: true })
}

// Re-read the signed-in user's profile (used after the username is claimed).
export async function refreshProfileNow(): Promise<void> {
  await refreshProfile()
}

let initialized = false
export function initAuth(): void {
  if (initialized) return
  initialized = true

  // Listener MUST be registered BEFORE getSession() so no auth event can slip
  // through between the initial session read and subscription. Any Supabase
  // call inside the callback is deferred with setTimeout(...,0): supabase-js
  // holds an internal lock while emitting events, and calling back into the
  // client synchronously (e.g. fetching the profile) can deadlock.
  supabase.auth.onAuthStateChange((event, session) => {
    setTimeout(() => {
      void setStateAsync({ session: session ?? null })
      if (event === 'SIGNED_IN') void maybeSendWelcomeEmail()
    }, 0)
  })

  supabase.auth.getSession().then(({ data }) => {
    setTimeout(() => {
      void setStateAsync({ session: data.session ?? null, ready: true })
    }, 0)
  })
}

// ── Welcome email (best-effort, once per account) ────────────────────────────
// signUp() marks the account as pending a welcome; the FIRST session that
// lands afterwards (email-confirm → sign-in, or instant session when
// confirmation is off) triggers the send-email edge function exactly once.
const WELCOME_PENDING_KEY = 'rr_welcome_pending'
const WELCOME_SENT_KEY = 'rr_welcome_sent'

async function maybeSendWelcomeEmail(): Promise<void> {
  try {
    if (localStorage.getItem(WELCOME_SENT_KEY)) {
      localStorage.removeItem(WELCOME_PENDING_KEY)
      return
    }
    if (localStorage.getItem(WELCOME_PENDING_KEY) !== '1') return
    localStorage.removeItem(WELCOME_PENDING_KEY)
    localStorage.setItem(WELCOME_SENT_KEY, '1')
    await supabase.functions.invoke('send-email', {
      body: {
        templateKey: 'welcome',
        params: { app_name: 'RoadRatings', app_url: window.location.origin },
      },
    })
  } catch {
    // Best-effort only — never block or surface a welcome-email failure.
  }
}

// Make sure the profile row exists before anything reads it (the DB trigger
// usually handles this; this covers sessions created before the trigger did).
async function setStateAsync(patch: Partial<typeof state>) {
  if (patch.session && patch.session !== state.session) {
    setState(patch)
    await refreshProfile()
  } else if (patch.session === null) {
    setState({ ...patch, isModerator: false, profileLoaded: false })
  } else {
    setState(patch)
  }
}

// ── Subscribe (for useSyncExternalStore) ─────────────────────────────────────

export function subscribeAuth(cb: () => void): () => void {
  listeners.add(cb)
  return () => listeners.delete(cb)
}

export function getAuthSnapshot(): typeof state {
  return state
}

export function useAuthReady(): boolean {
  return state.ready
}

// ── Actions ──────────────────────────────────────────────────────────────────

// Atomic username claim for social sign-ins. Returns null on success or a
// user-facing error string (mirrors the error text produced by the RPC).
export async function claimUsername(username: string): Promise<{ error: string | null }> {
  const { data, error } = await supabase.rpc('claim_username', {
    _username: username.trim(),
  })
  if (error) return { error: 'Could not set your username — please try again.' }
  return { error: (data as string | null) || null }
}

export async function checkUsernameAvailable(username: string): Promise<boolean> {
  const { data, error } = await supabase.rpc('username_available', { _username: username })
  if (error) return true // don't block signup on lookup failure
  return data === true
}

export async function signUp(input: {
  username: string
  email: string
  password: string
}): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.signUp({
    email: input.email.trim(),
    password: input.password,
    options: {
      data: { username: input.username.trim(), display_name: input.username.trim() },
      emailRedirectTo: window.location.origin,
    },
  })
  if (error) return { error: friendlyAuthError(error.message) }
  // Gate the one-time welcome email on the first successful session.
  try {
    localStorage.setItem(WELCOME_PENDING_KEY, '1')
  } catch {
    /* private mode etc. — welcome email simply won't send */
  }
  return { error: null }
}

export async function signIn(identifier: string, password: string): Promise<{ error: string | null }> {
  const id = identifier.trim()

  // Username or email — resolve usernames through the profiles table first.
  let email = id
  if (!id.includes('@')) {
    const { data, error: rpcError } = await supabase.rpc('login_identifier_to_email', {
      _identifier: id,
    })
    if (rpcError) return { error: 'Sign-in is temporarily unavailable. Try your email instead.' }
    if (!data) return { error: 'No account found with that username or email.' }
    email = data as string
  }

  const { error } = await supabase.auth.signInWithPassword({ email, password })
  return { error: error ? friendlyAuthError(error.message) : null }
}

/**
 * Resend the signup confirmation email. Every resend issues a FRESH
 * confirmation link, so a link burned by an email-provider scanner is never
 * fatal — the user just requests a new one.
 */
export async function resendConfirmation(email: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.resend({
    type: 'signup',
    email: email.trim(),
    options: { emailRedirectTo: window.location.origin },
  })
  if (error) {
    if (error.message.toLowerCase().includes('rate limit'))
      return { error: 'Too many attempts — please wait a moment and try again.' }
    return { error: friendlyAuthError(error.message) }
  }
  return { error: null }
}

/**
 * Send the 6-character backup code to the given email. Codes can't be
 * "consumed" by link-scanning email providers, so this is the scanner-proof
 * path in — delivered through the app's own email system, not the auth
 * template that only contains a link.
 */
export async function sendLoginCode(email: string): Promise<{ error: string | null }> {
  try {
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-login-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ action: 'send', email: email.trim() }),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok) return { error: (payload.error as string) || 'Could not send the code — try again.' }
    return { error: null }
  } catch {
    return { error: 'Could not send the code — check your connection and try again.' }
  }
}

/**
 * Confirm an account with the 6-character backup code. On success the
 * account's email is marked confirmed and the user is signed in directly
 * (the function returns a session, which we persist into the client).
 */
export async function confirmWithCode(
  email: string,
  code: string,
): Promise<{ error: string | null }> {
  const trimmed = code.trim()
  if (!/^[A-Za-z0-9]{6}$/.test(trimmed)) {
    return { error: 'Enter the 6-character code from your email.' }
  }
  try {
    const res = await fetch(`${import.meta.env.VITE_SUPABASE_URL}/functions/v1/email-login-code`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        apikey: import.meta.env.VITE_SUPABASE_ANON_KEY,
      },
      body: JSON.stringify({ action: 'verify', email: email.trim(), code: trimmed }),
    })
    const payload = await res.json().catch(() => ({}))
    if (!res.ok) return { error: (payload.error as string) || 'Could not verify the code — try again.' }
    if (payload.signed_in && payload.session) {
      const { error: setErr } = await supabase.auth.setSession({
        access_token: payload.session.access_token,
        refresh_token: payload.session.refresh_token,
      })
      if (setErr) return { error: 'Confirmed — but sign-in failed. Please sign in normally.' }
    }
    return { error: null }
  } catch {
    return { error: 'Could not verify the code — check your connection and try again.' }
  }
}

export async function signOut(): Promise<void> {
  // Clear the map state (selected road, route, ride) and close floating
  // panels BEFORE the session drops, so the UI returns to the bare map
  // regardless of how the sign-out is triggered.
  useRoadStore.getState().resetForSignOut()
  await supabase.auth.signOut()
}

export async function emailExists(email: string): Promise<boolean | null> {
  const { data, error } = await supabase.rpc('email_exists', { _email: email.trim() })
  if (error) return null
  return !!data
}

export async function resetPassword(email: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.resetPasswordForEmail(email.trim(), {
    redirectTo: `${window.location.origin}/reset-password`,
  })
  if (error) {
    if (error.message.toLowerCase().includes('rate limit'))
      return { error: 'Too many attempts — please wait a moment and try again.' }
    return { error: friendlyAuthError(error.message) }
  }
  return { error: null }
}

export async function updatePassword(newPassword: string): Promise<{ error: string | null }> {
  const { error } = await supabase.auth.updateUser({ password: newPassword })
  return { error: error ? friendlyAuthError(error.message) : null }
}

// ── Saved routes (account-backed) ────────────────────────────────────────────

// Live change signal so open panels (e.g. the account panel) can refresh the
// saved-routes list the moment a route is saved or unsaved elsewhere.
const savedRoutesListeners = new Set<() => void>()
export function onSavedRoutesChanged(fn: () => void): () => void {
  savedRoutesListeners.add(fn)
  return () => savedRoutesListeners.delete(fn)
}
function notifySavedRoutesChanged() {
  savedRoutesListeners.forEach((fn) => fn())
}

export async function fetchSavedRoutes(): Promise<SavedRouteRow[]> {

  // Read the locally-stored session (no network round-trip) — a transient
  // network failure here must not throw an unhandled "Failed to fetch".
  try {
    return await fetchSavedRoutesInner()
  } catch {
    return []
  }
}

async function fetchSavedRoutesInner(): Promise<SavedRouteRow[]> {
  const { data } = await supabase.auth.getSession()
  const user = data.session?.user ?? null
  if (!user) return []
  const { data: routes, error } = await supabase
    .from('saved_routes')
    .select('id, user_id, road_ids, trims, created_at, sort_index, route_names(name)')
    .eq('user_id', user.id)
    .order('created_at', { ascending: false })
  if (error) return []
  const rows = (routes ?? []).map((row) => {
    const names = (row as { route_names?: { name: string }[] | null }).route_names
    return {
      id: row.id,
      user_id: row.user_id,
      road_ids: row.road_ids,
      saved_at: row.created_at,
      name: names?.[0]?.name ?? null,
      sort_index: row.sort_index ?? 0,
      trims:
        row.trims && typeof row.trims === 'object' && !Array.isArray(row.trims)
          ? (row.trims as import('@/lib/roads/trim').RouteTrims)
          : null,
    }
  })
  // Attach the shared (account-independent) mileage/time totals for each
  // saved route's road sequence.
  const stats = await fetchRouteStats(rows.map((r) => routeStatsKey(r.road_ids)))
  return rows.map((r) => {
    const s = stats.get(routeStatsKey(r.road_ids))
    return {
      ...r,
      stats_miles: s?.miles ?? null,
      stats_seconds: s?.seconds ?? null,
      stats_computed_at: s?.computed_at ?? null,
    } as SavedRouteRow
  })
}

// Per-user display name for a saved route: "[route UUID]-[user UUID]" row in
// route_names, upserted so renaming twice updates instead of duplicating.
export async function renameSavedRoute(
  routeId: string,
  userId: string,
  name: string,
): Promise<{ error: string | null }> {
  const trimmed = name.trim()
  if (!trimmed) return { error: 'Name cannot be empty.' }
  const { error } = await supabase.from('route_names').upsert({
    id: `${routeId}-${userId}`,
    route_id: routeId,
    user_id: userId,
    name: trimmed,
    updated_at: new Date().toISOString(),
  })
  if (error) return { error: 'Could not rename this route.' }
  notifySavedRoutesChanged()
  return { error: null }
}

export async function saveRoute(roadIds: string[]): Promise<{ error: string | null }> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return { error: 'not-signed-in' }
  const { error } = await supabase
    .from('saved_routes')
    .insert({ user_id: user.id, road_ids: [...roadIds] })
  if (error) {
    if (error.code === '23505') return { error: 'This route is already in your saved routes.' }
    return { error: 'Could not save this route.' }
  }
  notifySavedRoutesChanged()
  return { error: null }
}

// Save with the mandatory display name. On success returns the saved
// route's UUID (the route's primary server-side identity) plus its name;
// 'duplicate' when the user already has a saved route with that name.
export async function saveRouteNamed(
  roadIds: string[],
  name: string,
  routeId?: string | null,
  trims?: import('@/lib/roads/trim').RouteTrims | null,
): Promise<{ error: 'not-signed-in' | 'empty-name' | 'duplicate' | 'failed' | null; routeId: string | null }> {
  const trimsParam =
    trims && Object.keys(trims).length
      ? Object.fromEntries(
          Object.entries(trims)
            .filter(([, t]) => t && (t.head || t.tail || t.reversed))
            .map(([id, t]) => [
              id,
              {
                head: t.head ? { pos: t.head.pos, coord: t.head.coord } : null,
                tail: t.tail ? { pos: t.tail.pos, coord: t.tail.coord } : null,
                ...(t.reversed ? { reversed: true } : {}),
              },
            ]),
        )
      : null
  const call = (withRoadIds: boolean) =>
    supabase.rpc('save_route_named', {
      _road_ids: withRoadIds ? [...roadIds] : null,
      _route_id: withRoadIds ? null : (routeId ?? null),
      _name: name,
      _trims: trimsParam,
    })
  // No UUID yet (first save) → save through the road list; a known UUID →
  // update that row directly (with a road-list fallback for stale UUIDs).
  let { data, error } = await call(!routeId)
  if (error) {
    // Transient failure (e.g. the API layer briefly not recognizing a newly
    // deployed function) — retry once before giving up.
    await new Promise((r) => setTimeout(r, 600))
    ;({ data, error } = await call(!routeId))
  }
  if (error) return { error: 'failed', routeId: null }
  let result = (data as string | null) ?? 'failed'
  if (result === 'failed' && routeId) {
    // Stale UUID (the route was deleted elsewhere mid-session) — retry
    // through the road-list path, which finds or creates the row again.
    const retry = await call(true)
    if (!retry.error) result = (retry.data as string | null) ?? 'failed'
  }
  // Legacy/edge tokens (not UUIDs) mean an error result.
  const isUuid = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(result)
  if (!isUuid) {
    return { error: result as 'not-signed-in' | 'empty-name' | 'duplicate' | 'failed', routeId: null }
  }
  return { error: null, routeId: result }
}

// Removes the saved route with the given UUID (or, legacy, the one whose
// road list exactly matches the given order).
export async function unsaveRoute(roadIds: string[], routeId?: string | null): Promise<{ error: string | null }> {
  const {
    data: { user },
  } = await supabase.auth.getUser()
  if (!user) return { error: 'not-signed-in' }
  if (routeId) {
    const { error: delError } = await supabase.from('saved_routes').delete().eq('id', routeId)
    if (delError) return { error: 'Could not remove this route.' }
    notifySavedRoutesChanged()
    return { error: null }
  }
  const { data, error } = await supabase
    .from('saved_routes')
    .select('id, road_ids')
    .eq('user_id', user.id)
  if (error) return { error: 'Could not remove this route.' }
  const match = (data ?? []).find(
    (r) =>
      (r.road_ids as string[]).length === roadIds.length &&
      (r.road_ids as string[]).every((id, i) => id === roadIds[i]),
  )
  if (!match) return { error: null } // already gone — treat as success
  const { error: delError } = await supabase.from('saved_routes').delete().eq('id', match.id)
  if (delError) return { error: 'Could not remove this route.' }
  notifySavedRoutesChanged()
  return { error: null }
}

export async function deleteSavedRoute(id: string): Promise<void> {
  await supabase.from('saved_routes').delete().eq('id', id)
  notifySavedRoutesChanged()
}

/** Persist a per-user drag-reorder of the saved routes list. */
export async function reorderSavedRoutes(
  orderedIds: string[],
): Promise<{ error: string | null }> {
  const updates = orderedIds.map((id, i) => ({ id, sort_index: i }))
  const { error } = await supabase.from('saved_routes').upsert(updates, { onConflict: 'id' })
  if (error) return { error: 'Could not save the new order.' }
  return { error: null }
}
